import type { Action, BoundActions } from 'react-sweet-state';
import {
	createActionsHook,
	createHook,
	createStore,
	createContainer,
	createStateHook,
} from 'react-sweet-state';
import { useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';

import type { AiIssueCreateState } from '@atlassian/ai-issue-create';
import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { InsertStorageFragmentRequestPayload } from '../__types__/InsertStorageFragmentRequestPayload';
import type { JiraProject } from '../__types__/apiUtils';
import { transformStargateUrl } from '../utils/configureUtils';

import {
	useAiBulkNavigationGotoPage,
	type AiBulkNavigationPageType,
} from './AiBulkNavigationContextProvider';
import { messages } from '../messages';

export const AiStates = {
	ABORT: 'abort',
	LOADING: 'loading',
	ERROR: 'error',
	IDLE: 'idle',
} as const;

export const ErrorStates = {
	BULK_CREATION: 'bulk-issue-creation-failed',
	ISSUE_GENERATION: 'ai-issue-generation-failed',
	SITE_RETRIEVAL: 'jira-site-retreival-failed',
	PROJECT_RETRIEVAL: 'jira-project-retreival-failed',
	ISSUE_TYPE_RETRIEVAL: 'issue-type-retreival-failed',
	EMTPY: '',
} as const;

export type ErrorState = (typeof ErrorStates)[keyof typeof ErrorStates];
type NonEmptyErrorState = Exclude<ErrorState, typeof ErrorStates.EMTPY>;
export const ErrorMessageMap: Record<
	NonEmptyErrorState,
	{
		id: string;
		defaultMessage: string;
		description: string;
	}
> = {
	[ErrorStates.BULK_CREATION]: messages.bulkCreateAiErrorDescription,
	[ErrorStates.ISSUE_GENERATION]: messages.issueGenerationErrorDescription,
	[ErrorStates.SITE_RETRIEVAL]: messages.connectJiraProjectErrorDescription,
	[ErrorStates.PROJECT_RETRIEVAL]: messages.connectJiraProjectErrorDescription,
	[ErrorStates.ISSUE_TYPE_RETRIEVAL]: messages.connectJiraProjectErrorDescription,
};

export interface IssueType {
	iconUrl: string;
	name: string;
	id: string;
	description?: string;
	self?: string;
	subtask?: boolean;
}

export interface IssueObject {
	id: string;
	summary: string;
	description?: DocNode;
	issueType: IssueType;
}

// Still not fully known what the exact object shape that will be sent to us is
export type AiBulkIssuesListType = IssueObject[];

export type BulkCreateContextType = {
	aiBulkIssuesList: AiBulkIssuesListType;
	insertStorageFragmentRequestPayload?: InsertStorageFragmentRequestPayload;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	currentJiraSite: AvailableSite;
	currentJiraProject: JiraProject;
	currentIssueId: string;
	issueTypeHasRequiredFields: boolean;
	error: ErrorState;
};

type State = {
	aiBulkIssuesList: AiBulkIssuesListType;
	currentIssueId: string;
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	currentJiraSite: AvailableSite | undefined;
	currentJiraProject: JiraProject | undefined;
	currentDefaultIssueType: IssueType | undefined;
	issueTypeHasRequiredFields: boolean;
	error: ErrorState;
};

export type IssueCreateSidePanelActions = BoundActions<State, typeof bulkCreateActions>;

const initialBulkCreateState: State = {
	aiBulkIssuesList: [],
	currentIssueId: '',
	insertStorageFragmentRequestPayload: undefined,
	aiState: AiStates.IDLE,
	isCreatedWithAi: false,
	currentJiraSite: undefined,
	currentJiraProject: undefined,
	currentDefaultIssueType: undefined,
	issueTypeHasRequiredFields: false,
	error: '',
};

const bulkCreateActions = {
	setCurrentIssueId:
		(currentIssueId: State['currentIssueId']): Action<State> =>
		({ setState }) => {
			setState({ currentIssueId });
		},
	setAiBulkIssuesList:
		(aiBulkIssuesList: State['aiBulkIssuesList']): Action<State> =>
		({ setState }) => {
			setState({ aiBulkIssuesList });
		},
	setAiState:
		(aiState: State['aiState']): Action<State> =>
		({ setState }) => {
			setState({ aiState });
		},
	setIsCreatedWithAi:
		(isCreatedWithAi: State['isCreatedWithAi']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedWithAi });
		},
	setInsertStorageFragmentRequestPayload:
		(insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload): Action<State> =>
		({ setState }) => {
			setState({ insertStorageFragmentRequestPayload });
		},
	removeAiBulkIssue:
		(issueId: string): Action<State> =>
		({ setState, getState }) => {
			const { aiBulkIssuesList } = getState();
			setState({ aiBulkIssuesList: aiBulkIssuesList.filter((issue) => issue.id !== issueId) });
		},
	removeAiBulkIssueList:
		(issueIds: string[]): Action<State> =>
		({ setState, getState }) => {
			const { aiBulkIssuesList } = getState();
			setState({
				aiBulkIssuesList: aiBulkIssuesList.filter((issue) => !issueIds.includes(issue.id)),
			});
		},
	setCurrentJiraSite:
		(currentJiraSite: State['currentJiraSite']): Action<State> =>
		({ setState }) => {
			setState({ currentJiraSite });
		},
	setCurrentJiraProject:
		(currentJiraProject: State['currentJiraProject']): Action<State> =>
		({ setState }) => {
			setState({ currentJiraProject });
		},
	setNextIssueToEdit:
		(goToPage: (page: AiBulkNavigationPageType) => void): Action<State> =>
		({ setState, getState }) => {
			const { aiBulkIssuesList, currentIssueId } = getState();
			const currentIssueListIndex = aiBulkIssuesList.findIndex(
				(issue) => issue.id === currentIssueId,
			);
			if (currentIssueListIndex >= aiBulkIssuesList.length - 1) {
				setState({
					aiBulkIssuesList: aiBulkIssuesList.filter((issue) => issue.id !== currentIssueId),
				});
				void goToPage('default');
				return;
			}
			setState({
				currentIssueId: aiBulkIssuesList[currentIssueListIndex + 1].id,
				aiBulkIssuesList: aiBulkIssuesList.filter((issue) => issue.id !== currentIssueId),
			});
		},
	setCurrentDefaultIssueType:
		(currentDefaultIssueType: State['currentDefaultIssueType']): Action<State> =>
		({ setState }) => {
			setState({ currentDefaultIssueType });
		},
	setIssueTypeHasRequiredFields:
		(issueTypeHasRequiredFields: State['issueTypeHasRequiredFields']): Action<State> =>
		({ setState }) => {
			setState({ issueTypeHasRequiredFields });
		},
	setError:
		(error: State['error']): Action<State> =>
		({ setState }) => {
			setState({ error });
		},
};

const Store = createStore({
	initialState: {
		...initialBulkCreateState,
	},
	actions: bulkCreateActions,
	name: 'bulkCreateContext',
});

export const BulkCreateContainer = createContainer(Store);

export const useBulkCreateContext = createHook(Store);

const useActions = createActionsHook(Store);

export const useSetIsCreatedWithAi = () => {
	const { setIsCreatedWithAi } = useActions();
	return setIsCreatedWithAi;
};

export const useSetCurrentIssueId = () => {
	const { setCurrentIssueId } = useActions();
	return setCurrentIssueId;
};

export const useSetNextIssueToEdit = () => {
	const { setNextIssueToEdit } = useActions();
	const goToPage = useAiBulkNavigationGotoPage();
	return useCallback(() => void setNextIssueToEdit(goToPage), [goToPage, setNextIssueToEdit]);
};

export const useSetIssueTypeHasRequiredFields = () => {
	const { setIssueTypeHasRequiredFields } = useActions();
	return setIssueTypeHasRequiredFields;
};

export const useSetInsertStorageFragmentRequestPayload = () => {
	const { setInsertStorageFragmentRequestPayload } = useActions();
	return setInsertStorageFragmentRequestPayload;
};

export const useGetIssueType = createStateHook(Store, {
	selector: (state: State): IssueType => ({
		iconUrl: transformStargateUrl(state.currentDefaultIssueType?.iconUrl || ''),
		name: state.currentDefaultIssueType?.name || '',
		id: state.currentDefaultIssueType?.id || '',
	}),
});

export const useSetError = () => {
	const { setError } = useActions();
	return setError;
};
